import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { monitoringAPI, onboardingAPI } from '../../services/api';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import TrackingSnippetTabs from '../../components/integration/TrackingSnippetTabs';
import WordPressPluginCard from '../../components/integration/WordPressPluginCard';
import {
  AssetChecklist,
  OnboardingStep,
  REASON_TEXT,
  STATUS_META,
  invalidateOverviewCache,
  progressColor,
} from '../../components/onboarding/onboardingTypes';

// Steps whose evidence arrives on its own (verify ping, first page view, SDK
// events) — poll while one of them is still open.
const LIVE_STEPS = ['tracking', 'analytics', 'mobile_sdk'];
const POLL_MS = 5000;

const StatusIcon: React.FC<{ step: OnboardingStep; index: number }> = ({ step, index }) => {
  const meta = STATUS_META[step.status];
  if (step.status === 'action_needed') {
    return (
      <span
        className="d-inline-flex align-items-center justify-content-center fw-semibold"
        style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${step.warning ? '#dc2626' : '#c7d2fe'}`, color: step.warning ? '#dc2626' : '#4f46e5', fontSize: 13, flexShrink: 0 }}
      >
        {step.warning ? '!' : index + 1}
      </span>
    );
  }
  return <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: 26, lineHeight: 1, flexShrink: 0 }}></i>;
};

const OnboardingAssetPage: React.FC = () => {
  const { assetId: assetIdParam } = useParams();
  const assetId = parseInt(assetIdParam || '', 10);
  const navigate = useNavigate();
  const { assets, setCurrentAsset } = useAuth();
  const [data, setData] = useState<AssetChecklist | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [showOptional, setShowOptional] = useState(false);
  const [skipping, setSkipping] = useState<string | null>(null);
  const [skipReason, setSkipReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [installTab, setInstallTab] = useState<'script' | 'wordpress'>('script');
  const [newUrl, setNewUrl] = useState('');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const prevStatus = useRef<Record<string, string>>({});
  useDocumentTitle(data ? `Setup · ${data.asset.name}` : 'Setup');

  const apply = useCallback((d: AssetChecklist, announce: boolean) => {
    // Celebrate steps that just turned green.
    if (announce) {
      d.steps.forEach((s) => {
        if (prevStatus.current[s.key] === 'action_needed' && s.status === 'done') toast.success(`${s.title} — done!`);
      });
    }
    prevStatus.current = Object.fromEntries(d.steps.map((s) => [s.key, s.status]));
    setData(d);
    setLastChecked(new Date());
  }, []);

  const load = useCallback(async (announce = false) => {
    try {
      const r = await onboardingAPI.get(assetId);
      apply(r.data.data, announce);
      setError(null);
      return r.data.data as AssetChecklist;
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load the setup checklist');
      return null;
    }
  }, [assetId, apply]);

  // Initial load: mark started, open the first open required step.
  useEffect(() => {
    if (!Number.isFinite(assetId)) { setError('Unknown asset'); return; }
    (async () => {
      const d = await load();
      if (!d) return;
      if (d.can_edit) onboardingAPI.start(assetId).catch(() => undefined);
      const first = d.steps.find((s) => s.required && s.status === 'action_needed') || d.steps.find((s) => s.status === 'action_needed');
      setOpen(first?.key || null);
      if (!d.steps.some((s) => s.required && s.status === 'action_needed')) setShowOptional(true);
    })();
  }, [assetId, load]);

  // Live verification polling (only while the tab is visible).
  const waitingLive = !!data?.steps.some((s) => LIVE_STEPS.includes(s.key) && s.status === 'action_needed');
  useEffect(() => {
    if (!waitingLive) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [waitingLive, load]);

  const run = async (fn: () => Promise<any>, ok?: string) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.data?.steps) apply(r.data.data, true);
      invalidateOverviewCache();
      if (ok) toast.success(ok);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Something went wrong');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Pages behind fix_route read the "current asset" from context — switch first.
  const goFix = (route: string, newTab = false) => {
    const a = assets.find((x) => x.id === assetId);
    if (a) setCurrentAsset(a);
    if (newTab) window.open(route, '_blank', 'noopener');
    else navigate(route);
  };

  const addUrl = async () => {
    const url = newUrl.trim();
    if (!url) return;
    try { new URL(url); } catch { toast.error('Enter a full URL, e.g. https://example.com'); return; }
    const ok = await run(() => monitoringAPI.addEndpoint(assetId, url), 'URL added to uptime monitoring');
    if (ok) { setNewUrl(''); load(true); }
  };
  const removeUrl = async (url: string) => {
    const ok = await run(() => monitoringAPI.removeEndpoint(assetId, url), 'URL removed');
    if (ok) load(true);
  };

  const skip = async (key: string) => {
    const ok = await run(() => onboardingAPI.skipStep(assetId, key, skipReason), 'Step skipped');
    if (ok) { setSkipping(null); setSkipReason(''); }
  };

  const finish = async () => {
    const ok = await run(() => onboardingAPI.complete(assetId), 'Setup complete — thanks!');
    if (ok) navigate('/onboarding');
  };

  const snooze = async () => {
    const ok = await run(() => onboardingAPI.snooze(assetId), "We'll remind you in 3 days");
    if (ok) navigate('/dashboard');
  };

  if (error) {
    return (
      <DashboardLayout noAssetRequired>
        <div className="container py-4" style={{ maxWidth: 860 }}>
          <div className="alert alert-warning">{error}</div>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/onboarding')}>Back to all assets</button>
        </div>
      </DashboardLayout>
    );
  }
  if (!data) {
    return (
      <DashboardLayout noAssetRequired>
        <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
      </DashboardLayout>
    );
  }

  const canEdit = data.can_edit !== false;
  const required = data.steps.filter((s) => s.required && s.status !== 'not_applicable');
  const optional = data.steps.filter((s) => !s.required && s.status !== 'not_applicable');
  const notApplicable = data.steps.filter((s) => s.status === 'not_applicable');
  const pct = data.progress.percent;
  const completedOnCurrent = !!data.state.completed_at && data.state.version >= data.state.current_version;

  const renderBody = (s: OnboardingStep) => {
    switch (s.key) {
      case 'tracking':
        return (
          <>
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <button className={`btn btn-sm ${installTab === 'script' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setInstallTab('script')}>
                <i className="bi bi-code-square me-1"></i>Tracking script
              </button>
              <button className={`btn btn-sm ${installTab === 'wordpress' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setInstallTab('wordpress')}>
                <i className="bi bi-wordpress me-1"></i>WordPress plugin
              </button>
            </div>
            {installTab === 'script' && data.integration ? (
              <TrackingSnippetTabs integrationData={data.integration} />
            ) : installTab === 'wordpress' ? (
              <WordPressPluginCard />
            ) : null}
          </>
        );
      case 'analytics': {
        const url = (data.steps.find((x) => x.key === 'monitoring')?.details?.urls || [])[0];
        return (
          <p className="small mb-2">
            Open any page of your site{url ? <> — <a href={url} target="_blank" rel="noreferrer">{url}</a></> : ''} in a new tab.
            The first page view turns this step green within seconds.
          </p>
        );
      }
      case 'monitoring': {
        const urls: string[] = s.details?.urls || [];
        return (
          <>
            {urls.length > 0 && (
              <ul className="list-group list-group-flush mb-2">
                {urls.map((u) => (
                  <li key={u} className="list-group-item px-0 d-flex justify-content-between align-items-center small">
                    <span className="font-monospace text-truncate">{u}</span>
                    {canEdit && (
                      <button className="btn btn-sm btn-link text-danger p-0" disabled={busy} onClick={() => removeUrl(u)} title="Stop monitoring">
                        <i className="bi bi-x-lg"></i>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canEdit && (
              <div className="input-group input-group-sm" style={{ maxWidth: 520 }}>
                <input
                  className="form-control"
                  placeholder="https://www.example.com"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addUrl(); }}
                />
                <button className="btn btn-primary" disabled={busy || !newUrl.trim()} onClick={addUrl}>Add URL</button>
              </div>
            )}
          </>
        );
      }
      case 'notifications':
        return (
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-sm btn-outline-primary" onClick={() => goFix(s.fix_route, true)}>
              <i className="bi bi-box-arrow-up-right me-1"></i>Open notification settings
            </button>
            {canEdit && s.status !== 'done' && (
              <button className="btn btn-sm btn-success" disabled={busy} onClick={() => run(() => onboardingAPI.reviewStep(assetId, 'notifications'), 'Marked as reviewed')}>
                <i className="bi bi-check2 me-1"></i>I've reviewed my notifications
              </button>
            )}
          </div>
        );
      case 'mobile_sdk':
        return (
          <button className="btn btn-sm btn-primary" onClick={() => goFix(s.fix_route)}>
            <i className="bi bi-phone me-1"></i>Open the mobile app setup wizard
          </button>
        );
      default:
        return (
          <button className="btn btn-sm btn-outline-primary" onClick={() => goFix(s.fix_route)}>
            Go to {s.title.toLowerCase()} <i className="bi bi-arrow-right ms-1"></i>
          </button>
        );
    }
  };

  const renderStep = (s: OnboardingStep, i: number) => {
    const isOpen = open === s.key;
    const meta = STATUS_META[s.status];
    const isLive = LIVE_STEPS.includes(s.key) && s.status === 'action_needed';
    return (
      <div key={s.key} className="card border-0 shadow-sm mb-2" style={{ borderLeft: `3px solid ${s.warning ? '#dc2626' : meta.color}` }}>
        <button
          type="button"
          className="card-body d-flex align-items-center gap-3 text-start bg-transparent border-0 w-100"
          onClick={() => setOpen(isOpen ? null : s.key)}
          aria-expanded={isOpen}
        >
          <StatusIcon step={s} index={i} />
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="fw-semibold">{s.title}</div>
            <div className="small" style={{ color: s.warning ? '#b91c1c' : '#64748b' }}>
              {s.status === 'skipped' ? `Skipped${s.skip_reason ? `: ${s.skip_reason}` : ''}` : s.evidence}
            </div>
          </div>
          {isLive && (
            <span className="small text-primary d-none d-md-inline">
              <span className="spinner-grow spinner-grow-sm me-1" style={{ width: 8, height: 8 }} />listening
            </span>
          )}
          <span className="badge" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} text-muted`}></i>
        </button>
        {isOpen && (
          <div className="card-body pt-0" style={{ paddingLeft: 60 }}>
            <p className="small text-muted mb-3">{s.description}</p>
            {s.status !== 'skipped' && renderBody(s)}
            {isLive && (
              <div className="small text-muted mt-3">
                <span className="spinner-border spinner-border-sm me-2" style={{ width: 12, height: 12 }} />
                Waiting for your site… we check every few seconds{lastChecked ? ` (last check ${lastChecked.toLocaleTimeString()})` : ''}.
                <button className="btn btn-link btn-sm p-0 ms-2" onClick={() => load(true)}>Check now</button>
              </div>
            )}
            {canEdit && (
              <div className="mt-3">
                {s.status === 'skipped' ? (
                  <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => run(() => onboardingAPI.resetStep(assetId, s.key), 'Step re-opened')}>
                    <i className="bi bi-arrow-counterclockwise me-1"></i>Undo skip
                  </button>
                ) : s.status === 'action_needed' && skipping !== s.key ? (
                  <button className="btn btn-sm btn-link text-muted p-0" onClick={() => { setSkipping(s.key); setSkipReason(''); }}>
                    Skip this step
                  </button>
                ) : null}
                {skipping === s.key && (
                  <div className="d-flex gap-2 align-items-center flex-wrap mt-1">
                    <input
                      className="form-control form-control-sm"
                      style={{ maxWidth: 360 }}
                      placeholder="Why are you skipping? (optional)"
                      value={skipReason}
                      maxLength={500}
                      onChange={(e) => setSkipReason(e.target.value)}
                    />
                    <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => skip(s.key)}>Skip</button>
                    <button className="btn btn-sm btn-link text-muted" onClick={() => setSkipping(null)}>Cancel</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout noAssetRequired>
      <div className="container py-3" style={{ maxWidth: 860 }}>
        <button className="btn btn-link btn-sm text-muted p-0 mb-2" onClick={() => navigate('/onboarding')}>
          <i className="bi bi-arrow-left me-1"></i>All assets
        </button>

        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div>
            <h1 className="h4 mb-1">Set up {data.asset.name}</h1>
            <div className="small text-muted">
              {data.reason ? REASON_TEXT[data.reason] : completedOnCurrent ? 'Setup complete — everything below is verified live.' : ''}
            </div>
          </div>
          <div className="text-end" style={{ minWidth: 200 }}>
            <div className="small text-muted mb-1">{pct}% complete · required {data.progress.required_complete}/{data.progress.required_total}</div>
            <div className="progress" style={{ height: 8 }}>
              <div className="progress-bar" style={{ width: `${pct}%`, background: progressColor(pct) }} />
            </div>
          </div>
        </div>

        {!canEdit && (
          <div className="alert alert-light border small py-2">You have view-only access — an owner or admin can complete these steps.</div>
        )}

        <h2 className="h6 text-uppercase text-muted mt-4 mb-2" style={{ letterSpacing: '0.05em' }}>Required</h2>
        {required.map((s, i) => renderStep(s, i))}

        {optional.length > 0 && (
          <>
            <button
              className="btn btn-link text-decoration-none text-muted p-0 mt-3 mb-2 h6 text-uppercase"
              style={{ letterSpacing: '0.05em', fontSize: '0.85rem' }}
              onClick={() => setShowOptional(!showOptional)}
              aria-expanded={showOptional}
            >
              <i className={`bi bi-chevron-${showOptional ? 'down' : 'right'} me-1`}></i>
              Optional — recommended ({data.progress.optional_complete}/{data.progress.optional_total} done)
            </button>
            {showOptional && optional.map((s, i) => renderStep(s, required.length + i))}
          </>
        )}

        {notApplicable.length > 0 && (
          <div className="small text-muted mt-2">
            Not applicable for this asset: {notApplicable.map((s) => s.title).join(', ')}.
          </div>
        )}

        {canEdit && (
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4 pt-3 border-top">
            <button className="btn btn-link text-muted btn-sm" disabled={busy} onClick={snooze}>Remind me later</button>
            <div className="d-flex gap-2 align-items-center">
              {!data.required_complete && <span className="small text-muted">Finish or skip the required steps to complete setup.</span>}
              {completedOnCurrent && !data.needs_onboarding ? (
                <button className="btn btn-outline-primary" disabled={busy} onClick={() => run(() => onboardingAPI.restart(assetId), 'Setup re-opened')}>
                  <i className="bi bi-arrow-repeat me-1"></i>Re-run setup
                </button>
              ) : (
                <button className="btn btn-primary" disabled={busy || !data.required_complete} onClick={finish}>
                  <i className="bi bi-check2-circle me-1"></i>Finish setup
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OnboardingAssetPage;
